export const SCOPES = {
    super: 0,
    system: 1,
    account: 2,
    own: 3,
} as const;

export type Scope = keyof typeof SCOPES;

export function isValidScope(scope: string): scope is Scope {
    return Object.keys(SCOPES).includes(scope as Scope);
}

export type PermissionSchema = {
    readonly resource: string;
    permission: string;
    scope: Scope;
}


export class Permissions {
    private permissions: Map<string, Array<{ permission: string; scope: Scope }>> = new Map();

    constructor(permissions: PermissionSchema[]) {
        if (!Array.isArray(permissions)) {
            throw new Error("Permissions must be an array");
        }
        this.permissions = this.buildPermissionObject(permissions);
    }

    public has(path: string, scope: Scope): boolean {
        const parts = path.split('.');
        const resource = parts[0];
        const iswildcard = path.endsWith('.*');

        if (parts.length < 2) {
            throw new Error("Invalid permission string");
        }

        if (!isValidScope(scope)) {
            throw new Error(`Invalid scope: ${scope}`);
        }

        if (this.permissions.size === 0) {
            return false;
        }

        if (!this.permissions.has(resource)) {
            return false;
        }

        const resourcePerms = this.permissions.get(resource)!;
        const length = iswildcard ? parts.length - 1 : parts.length;

        for (const { permission: permStr, scope: permScope } of resourcePerms) {
            for (let i = length; i > 1; i--) {
                const candidate = parts.slice(0, i).join('.');

                if (permStr === candidate) {
                    return SCOPES[permScope] <= SCOPES[scope];
                }
            }

            if (iswildcard) {
                if (permStr.startsWith(path.slice(0, -1))) {
                    return SCOPES[permScope] <= SCOPES[scope];
                }
            }
        }

        return false;
    }

    private buildPermissionObject(permissions: PermissionSchema[]): typeof this.permissions {
        return permissions.reduce((acc, curr) => {
            const resource = curr.resource.split('.')[0];
            const existing = acc.get(resource);

            const entry = {
                permission: curr.permission,
                scope: curr.scope,
            };

            if (existing) {
                existing.push(entry);
            } else {
                acc.set(resource, [entry]);
            }

            return acc;
        }, new Map() as typeof this.permissions);
    }
}

export function createPermissionsFromApi(apiPermissions: { resource: string; permission: string; scope: string }[]): Permissions {
    return new Permissions(apiPermissions as PermissionSchema[]);
}
